import React, { memo, useEffect, useRef, useState } from 'react';
import { classNames } from '../../utils/classNames';

export const MultiSelect = memo(function MultiSelect({
	options = [],
	value = [],
	onChange,
	placeholder = 'Select',
	label = 'item',
}) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);

	useEffect(() => {
		function handleClickOutside(event) {
			if (!containerRef.current) return;
			if (!containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		}

		function handleEscape(event) {
			if (event.key === 'Escape') {
				setOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	const selectedOptions = options.filter((option) =>
		value.includes(String(option.value)),
	);

	const buttonLabel = selectedOptions.length
		? `${selectedOptions.length} ${label}${selectedOptions.length > 1 ? 's' : ''} selected`
		: placeholder;

	function toggleOption(optionValue) {
		const normalizedValue = String(optionValue);

		if (value.includes(normalizedValue)) {
			onChange(value.filter((item) => item !== normalizedValue));
		} else {
			onChange([...value, normalizedValue]);
		}
	}

	function clearAll(event) {
		event.stopPropagation();
		onChange([]);
	}

	return (
		<div ref={containerRef} className='relative min-w-[240px]'>
			<button
				type='button'
				onClick={() => setOpen((previous) => !previous)}
				className={classNames(
					'h-9 w-full rounded-lg bg-white px-3 pr-9 text-left text-xs text-zinc-800 shadow-sm outline-none',
					'focus:border-sky-300 focus:ring-4 focus:ring-sky-100',
					'hover:bg-zinc-50',
				)}
				style={{ border: '1px solid #e4e4e7' }}
			>
				<span className='block truncate'>{buttonLabel}</span>
			</button>

			<div className='pointer-events-none absolute inset-y-0 right-2 flex items-center gap-1'>
				{value.length > 0 && (
					<button
						type='button'
						onClick={clearAll}
						className='pointer-events-auto inline-flex h-5 w-5 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600'
						tabIndex={-1}
					>
						<svg width='14' height='14' viewBox='0 0 20 20' fill='currentColor'>
							<path
								fillRule='evenodd'
								d='M4.22 4.22a.75.75 0 011.06 0L10 8.94l4.72-4.72a.75.75 0 111.06 1.06L11.06 10l4.72 4.72a.75.75 0 11-1.06 1.06L10 11.06l-4.72 4.72a.75.75 0 01-1.06-1.06L8.94 10 4.22 5.28a.75.75 0 010-1.06z'
								clipRule='evenodd'
							/>
						</svg>
					</button>
				)}

				<svg
					className='text-zinc-400'
					width='18'
					height='18'
					viewBox='0 0 20 20'
					fill='currentColor'
				>
					<path
						fillRule='evenodd'
						d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z'
						clipRule='evenodd'
					/>
				</svg>
			</div>

			{open && (
				<div className='absolute z-30 mt-2 w-full overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg'>
					<div className='max-h-64 overflow-auto p-1'>
						{options.length === 0 ? (
							<div className='px-3 py-2 text-xs text-zinc-500'>No options</div>
						) : (
							options.map((option) => {
								const checked = value.includes(String(option.value));

								return (
									<button
										key={String(option.value)}
										type='button'
										onClick={() => toggleOption(option.value)}
										className={classNames(
											'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs',
											checked
												? 'bg-sky-50 text-sky-700'
												: 'text-zinc-700 hover:bg-zinc-50',
										)}
									>
										<div
											className={classNames(
												'flex h-4 w-4 items-center justify-center rounded border',
												checked
													? 'border-sky-500 bg-sky-500 text-white'
													: 'border-zinc-300 bg-white',
											)}
										>
											{checked && (
												<svg
													width='12'
													height='12'
													viewBox='0 0 20 20'
													fill='currentColor'
												>
													<path
														fillRule='evenodd'
														d='M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 111.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z'
														clipRule='evenodd'
													/>
												</svg>
											)}
										</div>

										<span className='truncate'>{option.label}</span>
									</button>
								);
							})
						)}
					</div>
				</div>
			)}
		</div>
	);
});

export default MultiSelect;